import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const prodSnap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
        const prods = prodSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProducts(prods);

        const catSnap = await getDocs(collection(db, "productCategories"));
        setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  const getCount = (catName) => products.filter((p) => p.category === catName).length;

  const filtered = products.filter((p) => {
    const matchCategory = selectedCategory === "All" || p.category === selectedCategory;
    const matchSearch = !searchTerm || p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* HERO */}
      <section className="bg-[#f7d8e8]">
        <div className="max-w-[1400px] mx-auto h-[165px] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-[28px] md:text-[32px] font-extrabold tracking-wide uppercase leading-none">Shop</h1>
          <div className="mt-2 flex items-center gap-3 text-[13px] md:text-[14px]">
            <Link to="/" className="text-[#b8b8b8] hover:text-black transition">Home</Link>
            <span className="text-[#b8b8b8]">/</span>
            <span className="text-black">Shop</span>
          </div>
        </div>
      </section>

      <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-12 py-12">
        <div className="grid lg:grid-cols-[280px_1fr] gap-12">
          {/* SIDEBAR - Categories */}
          <aside>
            <h3 className="text-[16px] font-extrabold uppercase tracking-wide mb-5">Categories</h3>
            <ul className="space-y-1">
              <li>
                <button onClick={() => setSelectedCategory("All")}
                  className={`w-full text-left flex items-center justify-between px-4 py-2.5 text-[13px] font-semibold rounded-lg transition ${
                    selectedCategory === "All" ? "bg-[#e84fa5] text-white" : "text-gray-600 hover:bg-gray-50 hover:text-black"
                  }`}>
                  <span>All</span>
                  <span className="text-[11px] opacity-70">({products.length})</span>
                </button>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <button onClick={() => setSelectedCategory(cat.name)}
                    className={`w-full text-left flex items-center justify-between px-4 py-2.5 text-[13px] font-semibold rounded-lg transition ${
                      selectedCategory === cat.name ? "bg-[#e84fa5] text-white" : "text-gray-600 hover:bg-gray-50 hover:text-black"
                    }`}>
                    <span>{cat.name}</span>
                    <span className="text-[11px] opacity-70">({getCount(cat.name)})</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* PRODUCTS */}
          <div>
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <p className="text-lg">No products found.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search products..."
                    className="w-full max-w-md border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
                </div>
                <p className="text-[13px] text-gray-500 mb-6">
                  Showing {filtered.length} product{filtered.length !== 1 ? "s" : ""}
                  {selectedCategory !== "All" && ` in "${selectedCategory}"`}
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  {filtered.map((product) => (
                    <div key={product.id} className="group relative">
                      <Link to={`/product/${product.id}`}>
                        <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition relative">
                          <div className="aspect-square overflow-hidden bg-gray-100 relative">
                            <img
                              src={product.mainImage || (product.images && product.images[0]) || "/placeholder.png"}
                              alt={product.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center">
                              <span className="text-white text-[11px] font-bold uppercase bg-black/70 px-4 py-2 rounded opacity-0 group-hover:opacity-100 transition">
                                Quick View
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-[10px] text-[#e84fa5] font-bold uppercase mb-1 hover:underline">
                              {product.category || "Uncategorized"}
                            </p>
                            <h3 className="text-[12px] font-bold text-black leading-tight mb-2 line-clamp-2">{product.title}</h3>
                            <p className="text-[15px] font-extrabold text-black">${product.price}</p>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
